import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { format, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS, getApiUrl } from '../../config/api';

const DEPARTMENT = 'Ekspozicija';

type PlanType = 'onboarding' | 'qualification' | 'growth';

interface PlanTask {
  id: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  completed?: boolean;
}

interface PlanSection {
  id: string;
  title: string;
  sortOrder: number;
  tasks: PlanTask[];
  completedCount?: number;
  taskCount?: number;
}

interface PlanTemplate {
  id: string;
  name: string;
  type: PlanType;
  department: string;
  isActive: boolean;
  sections?: PlanSection[];
}

interface WorkerAssignmentSummary {
  assignmentId: string;
  templateId: string;
  templateName?: string;
  templateType?: string;
  status: string;
  completedTasks: number;
  totalTasks: number;
}

interface WorkerRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  position?: string;
  assignments: WorkerAssignmentSummary[];
}

interface AssignmentTree {
  id: string;
  status: string;
  userId: string;
  templateId: string;
  completedTasks: number;
  totalTasks: number;
  user?: { id: string; firstName: string; lastName: string };
  template?: { id: string; name: string; type: string };
  sections: PlanSection[];
}

interface AuditRow {
  id: string;
  actionType: string;
  entityType: string;
  entityId?: string;
  createdAt: string;
  actor?: { firstName: string; lastName: string };
  meta?: Record<string, unknown>;
}

type AuthHeaders = { Authorization: string };

function authHeaders(): AuthHeaders {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const StaffPlans: React.FC<{ embedded?: boolean }> = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, hasPermission } = useAuth();
  const canEdit = hasPermission('staff_plans.edit');
  const canView = hasPermission('staff_plans.view') || canEdit;

  const [innerTab, setInnerTab] = useState<'workers' | 'templates' | 'logs'>('workers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  const [templates, setTemplates] = useState<PlanTemplate[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [assignment, setAssignment] = useState<AssignmentTree | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PlanTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'onboarding' as PlanType,
    isActive: true
  });

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ title: '', sortOrder: 0 });
  const [sectionTemplateId, setSectionTemplateId] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<PlanSection | null>(null);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', sortOrder: 0 });
  const [taskSectionId, setTaskSectionId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<PlanTask | null>(null);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTemplateId, setAssignTemplateId] = useState('');

  const [logs, setLogs] = useState<AuditRow[]>([]);
  const [logsMonth, setLogsMonth] = useState(format(startOfMonth(new Date()), 'yyyy-MM'));
  const [logsLoading, setLogsLoading] = useState(false);

  const selectedWorker = useMemo(
    () => workers.find((w) => w.id === selectedWorkerId) || null,
    [workers, selectedWorkerId]
  );

  const loadWorkers = useCallback(async () => {
    const res = await fetch(
      `${getApiUrl(API_ENDPOINTS.STAFF_PLANS_WORKERS)}?department=${encodeURIComponent(DEPARTMENT)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    setWorkers(json.data || []);
    return json.data as WorkerRow[];
  }, []);

  const loadTemplates = useCallback(async () => {
    const res = await fetch(
      `${getApiUrl(API_ENDPOINTS.STAFF_PLANS_TEMPLATES)}?department=${encodeURIComponent(DEPARTMENT)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    setTemplates(json.data || []);
  }, []);

  const loadAssignment = useCallback(async (assignmentId: string) => {
    setAssignmentLoading(true);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.STAFF_PLANS_ASSIGNMENT(assignmentId)), {
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setAssignment(json.data);
    } finally {
      setAssignmentLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    if (!canEdit) return;
    setLogsLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl(API_ENDPOINTS.STAFF_PLANS_LOGS)}?month=${encodeURIComponent(logsMonth)}&limit=100`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setLogs(json.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  }, [canEdit, logsMonth]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await loadWorkers();
        if (cancelled) return;
        if (canView || canEdit) {
          await loadTemplates();
        }
        if (!cancelled && list.length > 0) {
          const preferSelf = list.find((w) => w.id === user?.id);
          setSelectedWorkerId(preferSelf?.id || list[0].id);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadWorkers, loadTemplates, canView, canEdit, user?.id]);

  useEffect(() => {
    if (!selectedWorker) {
      setAssignment(null);
      return;
    }
    const primary = selectedWorker.assignments[0];
    if (primary) {
      loadAssignment(primary.assignmentId);
    } else {
      setAssignment(null);
    }
  }, [selectedWorker, loadAssignment]);

  useEffect(() => {
    if (innerTab === 'logs' && canEdit) {
      loadLogs();
    }
  }, [innerTab, canEdit, loadLogs]);

  const refreshAfterMutation = async () => {
    await loadWorkers();
    await loadTemplates();
    if (assignment?.id) {
      await loadAssignment(assignment.id);
    }
  };

  const toggleTask = async (taskId: string, completed: boolean) => {
    if (!assignment) return;
    const canTick = canEdit || assignment.userId === user?.id;
    if (!canTick) return;
    try {
      const res = await fetch(
        getApiUrl(API_ENDPOINTS.STAFF_PLANS_ASSIGNMENT_TASK(assignment.id, taskId)),
        {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed })
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setAssignment(json.data);
      await loadWorkers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update task');
    }
  };

  const completeSection = async (sectionId: string) => {
    if (!assignment || !canEdit) return;
    try {
      const res = await fetch(
        getApiUrl(API_ENDPOINTS.STAFF_PLANS_SECTION_COMPLETE(assignment.id, sectionId)),
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setAssignment(json.data);
      await loadWorkers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to complete section');
    }
  };

  const saveTemplate = async () => {
    try {
      const body = {
        name: templateForm.name.trim(),
        type: templateForm.type,
        department: DEPARTMENT,
        isActive: templateForm.isActive
      };
      const url = editingTemplate
        ? getApiUrl(API_ENDPOINTS.STAFF_PLANS_TEMPLATE(editingTemplate.id))
        : getApiUrl(API_ENDPOINTS.STAFF_PLANS_TEMPLATES);
      const res = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await res.text());
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save template');
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!window.confirm(t('staffPlans.confirmDeleteTemplate', 'Ištrinti šabloną?'))) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.STAFF_PLANS_TEMPLATE(id)), {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      await loadTemplates();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete template');
    }
  };

  const saveSection = async () => {
    try {
      const body = {
        title: sectionForm.title.trim(),
        sortOrder: Number(sectionForm.sortOrder) || 0,
        ...(editingSection ? {} : { templateId: sectionTemplateId })
      };
      const url = editingSection
        ? getApiUrl(API_ENDPOINTS.STAFF_PLANS_SECTION(editingSection.id))
        : getApiUrl(API_ENDPOINTS.STAFF_PLANS_SECTIONS);
      const res = await fetch(url, {
        method: editingSection ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await res.text());
      setSectionDialogOpen(false);
      setEditingSection(null);
      await refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save section');
    }
  };

  const deleteSection = async (id: string) => {
    if (!window.confirm(t('staffPlans.confirmDeleteSection', 'Ištrinti skyrių?'))) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.STAFF_PLANS_SECTION(id)), {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      await refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete section');
    }
  };

  const saveTask = async () => {
    try {
      const body = {
        title: taskForm.title.trim(),
        description: taskForm.description.trim() || null,
        sortOrder: Number(taskForm.sortOrder) || 0,
        ...(editingTask ? {} : { sectionId: taskSectionId })
      };
      const url = editingTask
        ? getApiUrl(API_ENDPOINTS.STAFF_PLANS_TASK(editingTask.id))
        : getApiUrl(API_ENDPOINTS.STAFF_PLANS_TASKS);
      const res = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await res.text());
      setTaskDialogOpen(false);
      setEditingTask(null);
      await refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save task');
    }
  };

  const deleteTask = async (id: string) => {
    if (!window.confirm(t('staffPlans.confirmDeleteTask', 'Ištrinti užduotį?'))) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.STAFF_PLANS_TASK(id)), {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(await res.text());
      await refreshAfterMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete task');
    }
  };

  const assignPlan = async () => {
    if (!selectedWorkerId || !assignTemplateId) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.STAFF_PLANS_ASSIGNMENTS), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedWorkerId, templateId: assignTemplateId })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Assign failed');
      }
      const json = await res.json();
      setAssignDialogOpen(false);
      setAssignTemplateId('');
      await loadWorkers();
      setAssignment(json.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to assign plan');
    }
  };

  const typeLabel = (type: string) => {
    if (type === 'qualification') return t('staffPlans.types.qualification', 'Kvalifikacija');
    if (type === 'growth') return t('staffPlans.types.growth', 'Augimas');
    return t('staffPlans.types.onboarding', 'Įvedimas');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('staffPlans.pageTitle', 'Darbuotojai')}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          ({DEPARTMENT})
        </Typography>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={innerTab}
        onChange={(_, v) => setInnerTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="workers" label={t('staffPlans.tabs.workers', 'Darbuotojai')} />
        {canEdit && (
          <Tab value="templates" label={t('staffPlans.tabs.templates', 'Planų šablonai')} />
        )}
        {canEdit && (
          <Tab value="logs" label={t('staffPlans.tabs.logs', 'Veiklos žurnalas')} />
        )}
      </Tabs>

      {innerTab === 'workers' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
            gap: 2,
            alignItems: 'start'
          }}
        >
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 560,
              overflow: 'auto'
            }}
          >
            <List dense disablePadding>
              {workers.map((w) => {
                const a = w.assignments[0];
                const progress = a ? `${a.completedTasks}/${a.totalTasks}` : '—';
                return (
                  <ListItemButton
                    key={w.id}
                    selected={w.id === selectedWorkerId}
                    onClick={() => setSelectedWorkerId(w.id)}
                  >
                    <ListItemText
                      primary={`${w.firstName} ${w.lastName}`}
                      secondary={
                        a
                          ? `${a.templateName || ''} · ${progress}`
                          : t('staffPlans.noPlan', 'Nėra plano')
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          <Box>
            {selectedWorker && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  {selectedWorker.firstName} {selectedWorker.lastName}
                </Typography>
                {assignment && (
                  <Chip
                    size="small"
                    label={`${assignment.completedTasks}/${assignment.totalTasks}`}
                  />
                )}
                {assignment?.template && (
                  <Chip size="small" variant="outlined" label={typeLabel(assignment.template.type)} />
                )}
                {canEdit && !assignment && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setAssignTemplateId(templates.find((x) => x.isActive)?.id || '');
                      setAssignDialogOpen(true);
                    }}
                  >
                    {t('staffPlans.assignPlan', 'Priskirti planą')}
                  </Button>
                )}
              </Box>
            )}

            {assignmentLoading && (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!assignmentLoading && !assignment && selectedWorker && (
              <Typography color="text.secondary">
                {t('staffPlans.noAssignmentHint', 'Šiam darbuotojui dar nepriskirtas planas.')}
              </Typography>
            )}

            {!assignmentLoading &&
              assignment?.sections.map((section) => (
                <Accordion
                  key={section.id}
                  expanded={expandedSection === section.id}
                  onChange={(_, exp) => setExpandedSection(exp ? section.id : false)}
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, pr: 1 }}>
                      <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{section.title}</Typography>
                      <Chip
                        size="small"
                        label={`${section.completedCount ?? 0}/${section.taskCount ?? section.tasks.length}`}
                      />
                      {canEdit && (
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            completeSection(section.id);
                          }}
                        >
                          {t('staffPlans.completeSection', 'Pažymėti visą skyrių')}
                        </Button>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {(section.tasks || []).map((task) => {
                      const canTick = canEdit || assignment.userId === user?.id;
                      return (
                        <Box
                          key={task.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Checkbox
                            checked={Boolean(task.completed)}
                            disabled={!canTick}
                            onChange={(_, checked) => toggleTask(task.id, checked)}
                          />
                          <Box sx={{ flexGrow: 1, pt: 0.5 }}>
                            <Typography variant="body1">{task.title}</Typography>
                            {task.description && (
                              <Typography variant="body2" color="text.secondary">
                                {task.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        </Box>
      )}

      {innerTab === 'templates' && canEdit && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({ name: '', type: 'onboarding', isActive: true });
                setTemplateDialogOpen(true);
              }}
            >
              {t('staffPlans.newTemplate', 'Naujas šablonas')}
            </Button>
          </Box>

          {templates.map((tpl) => (
            <Accordion key={tpl.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                  <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{tpl.name}</Typography>
                  <Chip size="small" label={typeLabel(tpl.type)} />
                  <Chip
                    size="small"
                    color={tpl.isActive ? 'success' : 'default'}
                    label={tpl.isActive ? t('staffPlans.active', 'Aktyvus') : t('staffPlans.inactive', 'Neaktyvus')}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTemplate(tpl);
                      setTemplateForm({
                        name: tpl.name,
                        type: tpl.type,
                        isActive: tpl.isActive
                      });
                      setTemplateDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTemplate(tpl.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ mb: 1 }}
                  onClick={() => {
                    setSectionTemplateId(tpl.id);
                    setEditingSection(null);
                    setSectionForm({ title: '', sortOrder: (tpl.sections?.length || 0) * 10 });
                    setSectionDialogOpen(true);
                  }}
                >
                  {t('staffPlans.addSection', 'Pridėti skyrių')}
                </Button>
                {(tpl.sections || []).map((section) => (
                  <Box
                    key={section.id}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.03)
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{section.title}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingSection(section);
                          setSectionForm({ title: section.title, sortOrder: section.sortOrder });
                          setSectionDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteSection(section.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setTaskSectionId(section.id);
                          setEditingTask(null);
                          setTaskForm({
                            title: '',
                            description: '',
                            sortOrder: (section.tasks?.length || 0) * 10
                          });
                          setTaskDialogOpen(true);
                        }}
                      >
                        {t('staffPlans.addTask', 'Pridėti užduotį')}
                      </Button>
                    </Box>
                    {(section.tasks || []).map((task) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          pl: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2">{task.title}</Typography>
                          {task.description && (
                            <Typography variant="caption" color="text.secondary">
                              {task.description}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingTask(task);
                            setTaskForm({
                              title: task.title,
                              description: task.description || '',
                              sortOrder: task.sortOrder
                            });
                            setTaskDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteTask(task.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {innerTab === 'logs' && canEdit && (
        <Box>
          <TextField
            type="month"
            size="small"
            label={t('staffPlans.logsMonth', 'Mėnuo')}
            value={logsMonth}
            onChange={(e) => setLogsMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          {logsLoading ? (
            <CircularProgress size={28} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('staffPlans.logsWhen', 'Kada')}</TableCell>
                  <TableCell>{t('staffPlans.logsWho', 'Kas')}</TableCell>
                  <TableCell>{t('staffPlans.logsAction', 'Veiksmas')}</TableCell>
                  <TableCell>{t('staffPlans.logsEntity', 'Objektas')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.createdAt).toLocaleString('lt-LT')}</TableCell>
                    <TableCell>
                      {row.actor
                        ? `${row.actor.firstName} ${row.actor.lastName}`
                        : '—'}
                    </TableCell>
                    <TableCell>{row.actionType}</TableCell>
                    <TableCell>
                      {row.entityType}
                      {row.meta && typeof row.meta === 'object'
                        ? ` · ${Object.values(row.meta).filter((v) => typeof v === 'string').slice(0, 2).join(' · ')}`
                        : ''}
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary">
                        {t('staffPlans.noLogs', 'Įrašų nėra.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Template dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingTemplate
            ? t('staffPlans.editTemplate', 'Redaguoti šabloną')
            : t('staffPlans.newTemplate', 'Naujas šablonas')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('staffPlans.templateName', 'Pavadinimas')}
            value={templateForm.name}
            onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t('staffPlans.templateType', 'Tipas')}</InputLabel>
            <Select
              label={t('staffPlans.templateType', 'Tipas')}
              value={templateForm.type}
              onChange={(e) =>
                setTemplateForm((f) => ({ ...f, type: e.target.value as PlanType }))
              }
            >
              <MenuItem value="onboarding">{typeLabel('onboarding')}</MenuItem>
              <MenuItem value="qualification">{typeLabel('qualification')}</MenuItem>
              <MenuItem value="growth">{typeLabel('growth')}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={templateForm.isActive}
                onChange={(_, v) => setTemplateForm((f) => ({ ...f, isActive: v }))}
              />
            }
            label={t('staffPlans.active', 'Aktyvus')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>{t('common.cancel', 'Atšaukti')}</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={!templateForm.name.trim()}>
            {t('common.save', 'Išsaugoti')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section dialog */}
      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingSection
            ? t('staffPlans.editSection', 'Redaguoti skyrių')
            : t('staffPlans.addSection', 'Pridėti skyrių')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('staffPlans.sectionTitle', 'Skyriaus pavadinimas')}
            value={sectionForm.title}
            onChange={(e) => setSectionForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
          />
          <TextField
            type="number"
            label={t('staffPlans.sortOrder', 'Eilės nr.')}
            value={sectionForm.sortOrder}
            onChange={(e) => setSectionForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialogOpen(false)}>{t('common.cancel', 'Atšaukti')}</Button>
          <Button variant="contained" onClick={saveSection} disabled={!sectionForm.title.trim()}>
            {t('common.save', 'Išsaugoti')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingTask
            ? t('staffPlans.editTask', 'Redaguoti užduotį')
            : t('staffPlans.addTask', 'Pridėti užduotį')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('staffPlans.taskTitle', 'Užduotis')}
            value={taskForm.title}
            onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
          />
          <TextField
            label={t('staffPlans.taskDescription', 'Aprašymas')}
            value={taskForm.description}
            onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            type="number"
            label={t('staffPlans.sortOrder', 'Eilės nr.')}
            value={taskForm.sortOrder}
            onChange={(e) => setTaskForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>{t('common.cancel', 'Atšaukti')}</Button>
          <Button variant="contained" onClick={saveTask} disabled={!taskForm.title.trim()}>
            {t('common.save', 'Išsaugoti')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('staffPlans.assignPlan', 'Priskirti planą')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('staffPlans.template', 'Šablonas')}</InputLabel>
            <Select
              label={t('staffPlans.template', 'Šablonas')}
              value={assignTemplateId}
              onChange={(e) => setAssignTemplateId(e.target.value)}
            >
              {templates
                .filter((x) => x.isActive)
                .map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.id}>
                    {tpl.name} ({typeLabel(tpl.type)})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>{t('common.cancel', 'Atšaukti')}</Button>
          <Button variant="contained" onClick={assignPlan} disabled={!assignTemplateId}>
            {t('staffPlans.assign', 'Priskirti')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPlans;
